import { TranslocoPageRouteSyncService } from '@/core/transloco/transloco-loader';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';

const COMPONENT_I18N = 'components.mat-table';

/**
 * Custom MatPaginatorIntl that uses Transloco for translations
 */
@Injectable()
export class MatTablePaginatorIntl extends MatPaginatorIntl {
  override changes = new Subject<void>();
  private transloco = inject(TranslocoService);
  private pageI18n = inject(TranslocoPageRouteSyncService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    super();

    this.transloco.langChanges$
      .pipe(
        startWith(this.transloco.getActiveLang()),
        switchMap(() => this.pageI18n.ensureKnownComponentBundle('mat-table')),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.translateLabels();
        this.changes.next();
      });
  }

  private translateLabels(): void {
    this.itemsPerPageLabel = this.transloco.translate(
      `${COMPONENT_I18N}.paginator.itemsPerPage`
    );
    this.nextPageLabel = this.transloco.translate(
      `${COMPONENT_I18N}.paginator.nextPage`
    );
    this.previousPageLabel = this.transloco.translate(
      `${COMPONENT_I18N}.paginator.previousPage`
    );
    this.firstPageLabel = this.transloco.translate(
      `${COMPONENT_I18N}.paginator.firstPage`
    );
    this.lastPageLabel = this.transloco.translate(
      `${COMPONENT_I18N}.paginator.lastPage`
    );

    this.getRangeLabel = (
      page: number,
      pageSize: number,
      length: number
    ): string => {
      if (length === 0 || pageSize === 0) {
        return this.transloco.translate(
          `${COMPONENT_I18N}.paginator.rangeEmpty`,
          { length }
        );
      }
      const start = page * pageSize + 1;
      const end = Math.min((page + 1) * pageSize, length);
      return this.transloco.translate(`${COMPONENT_I18N}.paginator.range`, {
        start,
        end,
        length,
      });
    };
  }
}
